import { useEffect, useRef } from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaAward,
  FaChevronLeft,
  FaChevronRight,
  FaCrown,
  FaExternalLinkAlt,
  FaFire,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTrophy,
} from "react-icons/fa";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Skeleton } from "../../ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  getSpecialLabel,
  getTabColumns,
  getTabDisplayName,
  truncateWallet,
} from "../leaderboardData";
import { formatNumber } from "../formatters";

const PAGE_SIZE = 10;

const renderRankBadge = (rank) => {
  if (rank === 1) {
    return (
      <span className="leaderboard-rank-badge leaderboard-rank-badge-gold">
        <FaCrown className="h-4 w-4" />
      </span>
    );
  }

  if (rank === 2) {
    return (
      <span className="leaderboard-rank-badge leaderboard-rank-badge-silver">
        <FaAward className="h-4 w-4" />
      </span>
    );
  }

  if (rank === 3) {
    return (
      <span className="leaderboard-rank-badge leaderboard-rank-badge-bronze">
        <FaAward className="h-4 w-4" />
      </span>
    );
  }

  return <span className="leaderboard-rank-text">#{rank}</span>;
};

const getSortIcon = (column, sortColumn, sortDirection) => {
  if (sortColumn !== column) {
    return <FaSort className="h-3 w-3 opacity-60" />;
  }

  return sortDirection === "asc" ? (
    <FaSortUp className="h-3 w-3 text-primary" />
  ) : (
    <FaSortDown className="h-3 w-3 text-primary" />
  );
};

const TabLabel = ({ tab }) => {
  if (tab === "dckslap") {
    return (
      <>
        <FaTrophy className="h-3.5 w-3.5 leaderboard-tab-icon" />
        <span className="leaderboard-tab-label leaderboard-tab-label-full">DCKSLAP</span>
        <span className="leaderboard-tab-label leaderboard-tab-label-short">DCK</span>
      </>
    );
  }

  if (tab === "gbof") {
    return (
      <>
        <FaFire className="h-3.5 w-3.5 leaderboard-tab-icon" />
        <span className="leaderboard-tab-label leaderboard-tab-label-full">GBOF</span>
        <span className="leaderboard-tab-label leaderboard-tab-label-short">GBOF</span>
      </>
    );
  }

  if (tab === "received") {
    return (
      <>
        <FaArrowDown className="h-3.5 w-3.5 leaderboard-tab-icon" />
        <span className="leaderboard-tab-label leaderboard-tab-label-full">Top Received</span>
        <span className="leaderboard-tab-label leaderboard-tab-label-short">Recv</span>
      </>
    );
  }

  return (
    <>
      <FaArrowUp className="h-3.5 w-3.5 leaderboard-tab-icon" />
      <span className="leaderboard-tab-label leaderboard-tab-label-full">Top Sent</span>
      <span className="leaderboard-tab-label leaderboard-tab-label-short">Sent</span>
    </>
  );
};

const renderSkeletonRows = (columnCount) =>
  Array.from({ length: PAGE_SIZE }).map((_, index) => (
    <TableRow key={`skeleton-row-${index}`}>
      <TableCell>
        <Skeleton className="h-6 w-10" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-40" />
      </TableCell>
      {Array.from({ length: columnCount }).map((_, columnIndex) => (
        <TableCell key={`skeleton-column-${index}-${columnIndex}`}>
          <Skeleton className="h-5 w-16 ml-auto" />
        </TableCell>
      ))}
    </TableRow>
  ));

const renderBodyRows = ({
  rows,
  connected,
  selectedAccount,
  activeTab,
  columns,
  currentPage,
}) => {
  if (rows.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={columns.length + 2} className="py-12 text-center">
          No data yet for {getTabDisplayName(activeTab)}.
        </TableCell>
      </TableRow>
    );
  }

  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(pageStart, pageStart + PAGE_SIZE);

  return pageRows.map((entry) => {
    const isCurrentUser =
      connected && Boolean(selectedAccount?.address) && entry.address === selectedAccount.address;
    const specialLabel = getSpecialLabel(entry.address);

    return (
      <TableRow
        key={`${activeTab}-${entry.address}-${entry.rank}`}
        className={isCurrentUser ? "leaderboard-row-current-user" : ""}
      >
        <TableCell>{renderRankBadge(entry.rank)}</TableCell>
        <TableCell>
          <div className="leaderboard-address-cell">
            <a
              href={`https://www.radxplorer.com/accounts/${entry.address}`}
              target="_blank"
              rel="noopener noreferrer"
              className="leaderboard-address-link"
            >
              {truncateWallet(entry.address)}
              <FaExternalLinkAlt className="h-2.5 w-2.5" />
            </a>
            {isCurrentUser ? <Badge className="text-xs">YOU</Badge> : null}
            {specialLabel ? (
              <Badge variant="secondary" className="text-xs">
                {specialLabel}
              </Badge>
            ) : null}
          </div>
        </TableCell>
        {columns.map((column) => (
          <TableCell key={column.key} className="text-right font-medium tabular-nums">
            {formatNumber(entry[column.key])}
          </TableCell>
        ))}
      </TableRow>
    );
  });
};

const LeaderboardTableSection = ({
  activeTab,
  onTabChange,
  rows,
  isLoading,
  error,
  hasCachedData,
  currentPage,
  onPageChange,
  sortColumn,
  sortDirection,
  onSort,
  currentMonthName,
  connected,
  selectedAccount,
}) => {
  const tabsListRef = useRef(null);
  const columns = getTabColumns(activeTab, currentMonthName);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  useEffect(() => {
    const tabsListElement = tabsListRef.current;
    if (!tabsListElement || typeof window === "undefined") {
      return;
    }

    if (
      typeof window.matchMedia !== "function" ||
      !window.matchMedia("(max-width: 520px)").matches
    ) {
      return;
    }

    const activeTrigger = tabsListElement.querySelector(
      ".leaderboard-tab-trigger[data-state='active']"
    );

    if (!activeTrigger) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const computedStyle = window.getComputedStyle(tabsListElement);
      const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(computedStyle.paddingRight) || 0;
      const leftEdge = activeTrigger.offsetLeft - paddingLeft - 8;
      const rightEdge =
        activeTrigger.offsetLeft + activeTrigger.offsetWidth + paddingRight + 8;
      const currentLeft = tabsListElement.scrollLeft;
      const viewportRight = currentLeft + tabsListElement.clientWidth;
      let nextLeft = currentLeft;

      if (leftEdge < currentLeft) {
        nextLeft = leftEdge;
      } else if (rightEdge > viewportRight) {
        nextLeft = rightEdge - tabsListElement.clientWidth;
      }

      const maxLeft = tabsListElement.scrollWidth - tabsListElement.clientWidth;
      const clampedLeft = Math.max(0, Math.min(nextLeft, maxLeft));

      if (Math.abs(clampedLeft - currentLeft) > 1) {
        tabsListElement.scrollTo({
          left: clampedLeft,
          behavior: "auto",
        });
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeTab]);

  return (
    <Card className="leaderboard-surface-card">
      <CardHeader className="pb-4">
        <div className="leaderboard-table-header">
          <CardTitle className="text-xl flex items-center gap-2">
            <FaTrophy className="text-primary" />
            Leaderboard Rankings
          </CardTitle>
          <p className="leaderboard-table-refresh">Updates every 10 minutes</p>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList ref={tabsListRef} className="leaderboard-tabs-list">
            <TabsTrigger
              value="dckslap"
              className="leaderboard-tab-trigger"
              title="DCKSLAP"
              aria-label="DCKSLAP leaderboard"
            >
              <TabLabel tab="dckslap" />
            </TabsTrigger>
            <TabsTrigger
              value="gbof"
              className="leaderboard-tab-trigger"
              title="GBOF"
              aria-label="GBOF leaderboard"
            >
              <TabLabel tab="gbof" />
            </TabsTrigger>
            <TabsTrigger
              value="received"
              className="leaderboard-tab-trigger"
              title="Top Received"
              aria-label="Top received leaderboard"
            >
              <TabLabel tab="received" />
            </TabsTrigger>
            <TabsTrigger
              value="sent"
              className="leaderboard-tab-trigger"
              title="Top Sent"
              aria-label="Top sent leaderboard"
            >
              <TabLabel tab="sent" />
            </TabsTrigger>
          </TabsList>

          <div className="leaderboard-table-shell">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-16">Rank</TableHead>
                  <TableHead>Address</TableHead>
                  {columns.map((column) => (
                    <TableHead
                      key={column.key}
                      className="text-right leaderboard-sort-head"
                      onClick={() => onSort(column.key)}
                    >
                      <span className="inline-flex items-center justify-end gap-1">
                        {column.label}
                        {getSortIcon(column.key, sortColumn, sortDirection)}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading
                  ? renderSkeletonRows(columns.length)
                  : error && !hasCachedData
                    ? (
                      <TableRow>
                        <TableCell colSpan={columns.length + 2} className="py-8 text-center">
                          <div className="space-y-2">
                            <p className="text-destructive">
                              Error loading data: {error?.message || String(error)}
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.location.reload()}
                            >
                              Retry
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                    : renderBodyRows({
                      rows,
                      connected,
                      selectedAccount,
                      activeTab,
                      columns,
                      currentPage,
                    })}
              </TableBody>
            </Table>
          </div>
        </Tabs>

        <div className="leaderboard-pagination">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange("prev")}
            disabled={currentPage <= 1 || isLoading}
            className="gap-2"
          >
            <FaChevronLeft className="h-3 w-3" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange("next")}
            disabled={currentPage >= totalPages || isLoading}
            className="gap-2"
          >
            Next
            <FaChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaderboardTableSection;
